import * as React from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { yupResolver } from '@hookform/resolvers';
import ReactInputMask from 'react-input-mask';
import { clearRequestError } from '../redux/ducks/userAuth';
import { registerAccount } from '../api/userAuth';
import { COMPANY_NAME, APPLICATION_NAME } from '../constants';
import AuthLayout from '../layouts/AuthLayout';
import { apiSlice } from '../redux/rtkQuery/apiSlice';
import { InputBaseContainer, InputTextWithLabel, PasswordInput } from '../molecules';
import { ButtonMain } from '../atoms';
import { ROUTES } from '../constants/routes';
import { emailRegex, schema } from './RegisterPageSchema';
import { checkAvailableEmail } from '../api/userSettings';

const RegisterPage = () => {
  const dispatch = useDispatch();
  const [password, setPassword] = useState<string>('');
  const [repassword, setRePassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [num1, setNum1] = useState<number>(0);
  const [num2, setNum2] = useState<number>(0);
  const [regDisabled, setRegDisabled] = useState<boolean>(true);
  const [typedResult, setTypedResult] = useState<string>('');
  const history = useHistory();
  const { handleSubmit, setError, getValues, errors, clearErrors, register, control, formState, watch, setValue } = useForm({
    resolver: yupResolver(schema),
    // defaultValues: {
    //   firstName: 'John',
    //   lastName: 'Doe',
    //   phone: '222-222-2222',
    //   address: '123 Main st. Naples FL 22222',
    //   email: 'a@b.com',
    // },

  });

  useEffect(() => {
    const num = Number(typedResult);
    if (!isNaN(num) && num !== 0) {
      setRegDisabled(num !== num1 + num2);
    } else if (num1 === 0 && num2 === 0 && typedResult === '0') {
      setRegDisabled(false);
    } else {
      setRegDisabled(true);
    }
  }, [typedResult]);

  const onResultChange = e => {
    const x = typedResult;
    if (/^\d+$/.test(e.target.value) || !e.target.value) {
      setTypedResult(e.target.value);
    }
  };
  React.useEffect(() => {
    dispatch(clearRequestError());
    dispatch(apiSlice.util.resetApiState());
    const _n1 = Math.floor(Math.random() * 10);
    const _n2 = Math.floor(Math.random() * 10);
    setNum1(_n1);
    setNum2(_n2);
  }, []);

  const onSubmit = async data => {
    if (!password) {
      setPasswordError('Password is required.');
      return;
    }
    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters.');
      return;
    }
    if (password !== repassword) {
      setPasswordError('Password confirmation mismatch.');
      return;
    }

    setPasswordError('');

    // Re-run email validation in case Chrome autofilled the field and onBlur never fired.
    const emailError = await validateEmail();
    if (emailError) return;

    const payload = {
      ...data,
      email: (data.email || '').trim().toLowerCase(),
      password,
    };

    const sendData = async () => {
      try {
        const result = await registerAccount(payload);
        setSuccess(result?.data?.message);
      } catch (error) {
        console.log(error);
      }
    };
    sendData();
  };

  const validateEmail = async (): Promise<string> => {
    const raw = getValues('email') || '';
    const email = raw.trim().toLowerCase();
    if (email !== raw) {
      setValue('email', email);
    }
    if (!email) {
      const msg = 'Email is required';
      setError('email', { message: msg });
      return msg;
    }
    if (!emailRegex.test(email)) {
      const msg = 'Invalid email format';
      setError('email', { message: msg });
      return msg;
    }
    clearErrors('email');

    const result = await checkAvailableEmail(email);
    if (result.is_company_email) {
      const msg = 'This email address is associated with your company rather than an individual user account. To access the Client Portal, please contact our office to have your personal login credentials created.';
      setError('email', { message: msg });
      return msg;
    }
    if (result.email_exists) {
      const msg = "Email already exists. Please use 'Forgot Password' to create or reset your password.";
      setError('email', { message: msg });
      return msg;
    }
    return '';
  };

  const goBack = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    history.push(ROUTES.LOG_IN);
  };
  return (
    <AuthLayout mainText={APPLICATION_NAME} companyText={COMPANY_NAME} secondaryText="Register a new account.">
      <FormContainer autoComplete="off" aria-autocomplete="none" onSubmit={handleSubmit(onSubmit)}>
        {!success && (
          <>
            <InputBaseContainer>
              <InputTextWithLabel isRequired name="firstName" error={errors.firstName} inputRef={register} label="First Name" />
            </InputBaseContainer>
            <InputBaseContainer>
              <InputTextWithLabel isRequired name="lastName" error={errors.lastName} inputRef={register} label="Last Name" />
            </InputBaseContainer>
            <InputBaseContainer>
              <ReactInputMask mask="999-999-9999" maskChar=" " name="phone">
                {() => <InputTextWithLabel isRequired error={errors.phone} name="phone" inputRef={register} label="Phone Number" />}
              </ReactInputMask>
            </InputBaseContainer>
            <InputBaseContainer>
              <InputTextWithLabel isRequired name="address" error={errors.address} inputRef={register} label="Address" />
            </InputBaseContainer>
            <InputBaseContainer>
              <InputTextWithLabel isRequired name="email" error={errors.email} inputRef={register} onBlur={validateEmail} label="Email" />
            </InputBaseContainer>
            <InputBaseContainer>
              <PasswordContainer>
                <Label>Password<Required>*</Required></Label>
                <PasswordInput
                  id="password"
                  label=""
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  isValid={!passwordError}
                  helperText={passwordError}
                />
              </PasswordContainer>
            </InputBaseContainer>
            <InputBaseContainer>
              <PasswordContainer>
                <Label>Confirm Password<Required>*</Required></Label>
                <PasswordInput id="password_test" label="" onChange={e => setRePassword(e.target.value)} value={repassword} />
              </PasswordContainer>
            </InputBaseContainer>
            <SpamProtection>
              <SpamHeader>
                <SpamIcon aria-hidden>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <path d="m9 12 2 2 4-4" />
                  </svg>
                </SpamIcon>
                <SpamTitle>Spam protection<Required>*</Required></SpamTitle>
              </SpamHeader>
              <SpamRow>
                <SpamPrompt htmlFor="spam-result">
                  What is <strong>{num1}</strong> + <strong>{num2}</strong>?
                </SpamPrompt>
                <SpamInput
                  id="spam-result"
                  name="result"
                  value={typedResult}
                  onChange={onResultChange}
                  placeholder="Answer"
                  inputMode="numeric"
                  autoComplete="off"
                />
              </SpamRow>
            </SpamProtection>
          </>
        )}
        {!success && (
          <Buttons>
            <ButtonMain onClick={goBack}>Back</ButtonMain>
            <RegisterButton>
              <ButtonMain type="submit" disabled={regDisabled}>
                Register
              </ButtonMain>
            </RegisterButton>
          </Buttons>
        )}

        {success && (
          <SuccessMessage>
            <div>{`${success}`}</div>
            <Goto>
              Go to <LoginLink onClick={() => history.push(ROUTES.LOG_IN)}>Login Page</LoginLink> to continue
            </Goto>
          </SuccessMessage>
        )}
      </FormContainer>
    </AuthLayout>
  );
};

export default RegisterPage;

const SuccessMessage = styled.div`
  margin-top: 38px;
  display: flex;
  flex-direction: column;
  font-size: 20px;
  color: green;
`;

const Goto = styled.div`
  display: flex;
  gap: 11px;
  color: var(--secondaryText);
  font-size: 15px;
  margin-top: 5px;
`;
const LoginLink = styled.div`
  text-decoration: underline;
  font-style: italic;
  cursor: pointer;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: center;
  gap: 50px;
  margin-top: 30px;
  margin-bottom: 7px;
  width: 100%;
`;

const RegisterButton = styled.div`
  width: 115px;
`;

const FormContainer = styled.form`
  width: 100%;
  @media only screen and (max-width: 840px) {
    width: 75vw !important;
  }
  .MuiButton-root {
    margin-top: 24px;
  }
  .MuiChip-outlined {
    margin-right: 5px;
    margin-top: 5px;
  }
`;

const PasswordContainer = styled.div`
  display: flex;
  flex-direction: column;
  .MuiOutlinedInput-root {
    height: 38px;
  }
`;

const Label = styled.div`
  font-size: 17px;
  margin-bottom: 6px;
`;

const Required = styled.span`
  color: var(--danger-500);
  margin-left: 2px;
`;

const SpamProtection = styled.div`
  margin-top: 20px;
  padding: 16px 18px;
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-sizing: border-box;
`;

const SpamHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const SpamIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: var(--primary-50);
  color: var(--primary-600);
`;

const SpamTitle = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-600);
`;

const SpamRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media only screen and (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
`;

const SpamPrompt = styled.label`
  flex: 1;
  font-size: 0.875rem;
  color: var(--mainText);
  line-height: 1.4;

  strong {
    font-weight: 700;
    color: var(--neutral-800);
  }
`;

const SpamInput = styled.input`
  width: 110px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--mainText);
  text-align: center;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  font-family: inherit;

  &::placeholder {
    color: var(--neutral-400);
    font-weight: 400;
  }

  &:hover {
    border-color: var(--neutral-400);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }

  @media only screen and (max-width: 480px) {
    width: 100%;
  }
`;
